import {
  EmptyRow, ErpTable, KpiStrip, MetricCard, Panel, Td, Th,
} from '@/components/erp/primitives'
import { BucketAxis, ChartFrame, OutcomeStrip, RankedBars } from '@/shared/ui/charts'
import { Note } from '@/shared/ui/states'
import { fmtDateTime, fmtNumber } from '@/shared/lib/format'
import type { ImpactResponse } from '@/shared/api/types'
import { analyticsImpact } from '../api'
import { Excluded, LiveNote, MeasuredWith, Unavailable } from '../parts'
import { categoryLabel, verdictLabel } from '../vocab'
import { AnalyticsScreen } from './shell'

/**
 * Did any of it work — at portfolio level.
 *
 * ## There is no single verdict here, on purpose
 *
 * A programme has a verdict. A portfolio of nine programmes has nine, and the
 * useful thing to say about it is the distribution: how many could be measured
 * at all, and what stopped the rest. Averaging nine verdicts into one word is
 * the thing this screen exists not to do, and `expectedUpliftPp` — the number
 * somebody typed at design time — is absent for the same reason: nine plans do
 * not combine into a portfolio plan honestly.
 *
 * ## Treated, control, and the difference between them
 *
 * `meanUpliftTreated` on its own is a before-and-after figure, and everything
 * that happened in those months is inside it — a campaign, a drought, a rival's
 * rally. `differenceInDifferences` subtracts the control cohort's movement over
 * the same window, which is the only figure here that supports a causal reading,
 * and only where a control exists. All three are shown side by side so the gap
 * between the naive figure and the corrected one is visible.
 */
export function ProgramImpactScreen() {
  return (
    <AnalyticsScreen
      title="Impact"
      description="What was measured, over how many people, and what could not be measured."
      load={(f) => analyticsImpact(f)}
      isEmpty={(d) => d.programmes === 0}
      exportKind="IMPACT"
    >
      {(d) => <ImpactBody d={d} />}
    </AnalyticsScreen>
  )
}

const VERDICT_TONE: Record<string, 'good' | 'warn' | 'bad' | 'neutral'> = {
  MEASURED: 'good',
  NO_CONTROL: 'warn',
  INSUFFICIENT_DATA: 'warn',
  TOO_SOON: 'neutral',
  NOT_MEASURABLE: 'neutral',
}

function ImpactBody({ d }: { d: ImpactResponse }) {
  const verdicts = Object.entries(d.byVerdict ?? {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({
      label: verdictLabel(k),
      value: v,
      tone: VERDICT_TONE[k] ?? 'neutral',
    }))

  // A bucket nobody was measured in is DROPPED, not drawn at zero. A zero-height
  // bar labelled "+0.0 pp" is the claim that the programme changed nothing in
  // that month — in the one file whose whole argument is that nulls are not
  // zeroes. How many were dropped is printed under the chart.
  const allBuckets = d.upliftByMonthsToMeasurement ?? []
  const buckets = allBuckets
    .filter((b) => b.meanUplift != null)
    .map((b) => ({
      label: `${b.monthsSinceEnrolment}m`,
      value: Number(b.meanUplift),
      hint: `${fmtNumber(b.measured)} measured`,
    }))
  const unmeasuredBuckets = allBuckets.length - buckets.length

  const did = d.differenceInDifferences == null ? null : Number(d.differenceInDifferences)
  const treated = d.meanUpliftTreated == null ? null : Number(d.meanUpliftTreated)
  const control = d.meanUpliftControl == null ? null : Number(d.meanUpliftControl)

  return (
    <div className="space-y-4">
      <KpiStrip>
        <MetricCard
          label="Programmes measured"
          value={`${fmtNumber(d.programmesMeasured)} / ${fmtNumber(d.programmes)}`}
          sub="The rest are missing an input, not failing"
        />
        <MetricCard
          label="Support gained where it ran"
          value={treated == null ? <Unavailable /> : <span className="tabular-nums">{treated > 0 ? '+' : ''}{treated.toFixed(1)} pp</span>}
          sub={<MeasuredWith measured={d.measured} measurable={d.measurable} />}
        />
        <MetricCard
          label="Support gained where it did not"
          value={control == null ? <Unavailable reason="No control cohort exists for the programmes in scope." short /> : <span className="tabular-nums">{control > 0 ? '+' : ''}{control.toFixed(1)} pp</span>}
          sub={<MeasuredWith measured={d.controlMeasured} measurable={d.controlSize} />}
        />
        <MetricCard
          label="Difference"
          value={did == null
            ? <Unavailable reason="No control cohort, so the treated figure cannot be corrected." short />
            : <span className="tabular-nums">{did > 0 ? '+' : ''}{did.toFixed(1)} pp</span>}
          accent
          sub={(
            <span title="Treated movement minus control movement over the same window. The only figure here that supports a causal reading.">
              treated minus control
            </span>
          )}
        />
      </KpiStrip>

      {did == null && treated != null && (
        <Note tone="bad">
          There is no control cohort, so the treated figure is a before-and-after and
          everything else that happened in those months is inside it. Read it as "support
          among these people moved by {treated.toFixed(1)} points", never as "the programme
          moved support by {treated.toFixed(1)} points".
        </Note>
      )}

      {d.explanation && <Note tone="info">{d.explanation}</Note>}

      <Panel title="What can be said about each programme"
        description="A verdict names the missing input, not a judgement about the programme.">
        <OutcomeStrip parts={verdicts} />
      </Panel>

      <Excluded
        items={[
          { count: d.excludedUnlinked, label: 'not linked to a voter record', hint: 'No baseline exists for a beneficiary who is not a voter record, because the baseline IS the voter record.' },
          { count: d.excludedNoBaseline, label: 'no baseline reading', hint: 'Enrolled before anybody recorded their support level.' },
          { count: d.excludedNotEnrolled, label: 'not enrolled at the measurement date', hint: 'Joined after the window closed.' },
        ]}
      />

      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}>
        <Panel
          title="By months to measurement"
          description="When the field team went back — not when the effect appeared."
        >
          <ChartFrame title="Mean uplift" description="percentage points, by months between enrolment and the re-assessment">
            <RankedBars
              points={buckets}
              formatValue={(n) => `${n > 0 ? '+' : ''}${n.toFixed(1)} pp`}
              emptyMessage="Nobody has been re-assessed yet."
            />
          </ChartFrame>
          {buckets.length > 0 && <BucketAxis points={buckets} />}
          {unmeasuredBuckets > 0 && (
            <p className="mt-2 text-erp-xs text-muted-foreground">
              {unmeasuredBuckets} month {unmeasuredBuckets === 1 ? 'bucket has' : 'buckets have'} no
              measured uplift and {unmeasuredBuckets === 1 ? 'is' : 'are'} not drawn.
            </p>
          )}
          <LiveNote>
            A rising line here is at least as likely to mean the programmes measured late
            were the ones going well as it is to mean effects grow. The buckets are when
            somebody went back, not when anything happened.
          </LiveNote>
        </Panel>

        <Panel title="By category" bodyClassName="p-0">
          <ErpTable head={(
            <>
              <Th>Category</Th>
              <Th className="text-right">Programmes</Th>
              <Th className="text-right">Mean uplift</Th>
              <Th className="text-right">Measured</Th>
            </>
          )}>
            {(d.categoryBenchmarks?.length ?? 0) === 0
              ? (
                <EmptyRow colSpan={4}
                  message={d.benchmarkUnavailableReason ?? 'No category has enough measured programmes to compare.'} />
              )
              : d.categoryBenchmarks.map((b) => (
                <tr key={b.key}>
                  <Td>{categoryLabel(b.key)}</Td>
                  <Td className="text-right tabular-nums">{fmtNumber(b.programmes)}</Td>
                  <Td className="text-right tabular-nums">
                    {b.meanUplift == null
                      ? <Unavailable short />
                      : `${Number(b.meanUplift) > 0 ? '+' : ''}${Number(b.meanUplift).toFixed(1)} pp`}
                  </Td>
                  <Td className="text-right tabular-nums">{fmtNumber(b.measured)}</Td>
                </tr>
              ))}
          </ErpTable>
        </Panel>
      </div>

      <p className="text-erp-xs text-muted-foreground">
        {fmtNumber(d.totalBeneficiaries)} beneficiaries in scope;{' '}
        <MeasuredWith measured={d.measured} measurable={d.measurable} /> of the ones a
        reading was possible for.
        {d.measuredAt && <> Last reading {fmtDateTime(d.measuredAt)}.</>}
      </p>
    </div>
  )
}
